import type { MemberDataFirestoreRecord } from "@albertine/types";
import type { CatalogItemFull } from "@albertine/types/openapi";
import { getLocale } from "albertine-shared-web";
import {
    type CatalogItem,
    catalogItemToCatalogItemPreview,
} from "../../common/CatalogItem";
import type { Agent } from "../../common/types/Agent";
import type { AttachmentWithFile } from "../../common/types/Attachment";
import type { Booking, BookingInMessage } from "../../common/types/Booking";
import type { Conversation } from "../../common/types/Conversation";
import type { AgentNewMessagePayload } from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import type { CatalogArticleItem } from "../../common/types/Storyblok";
import { partition, pushOrUpdate } from "../../common/utils/array.util";
import { conversationFileRef, supportedFileTypes } from "../../FirebaseBackend";
import { uploadFile } from "../../FirebaseBackendActions";
import { attachmentFromFile, randomUUIDFilename } from "../../utils/file.util";
import type { SetState, Stateful } from "../../utils/react.util";

export function addToBookingArray(
    booking: BookingInMessage,
    bookings: BookingInMessage[],
): BookingInMessage[] {
    return pushOrUpdate<BookingInMessage>(
        booking,
        (b1, b2) => b1.id === b2.id,
        bookings,
    );
}

export type MessageType = "message" | "internal_note";
export type MessageInputFunction = "send" | "edit";
export type MessageInputContent = {
    replyText: string | undefined;
    replyHtml: string | undefined;
    bookings: BookingInMessage[] | undefined;
    attachments: AttachmentWithFile[] | undefined;
    articles: CatalogArticleItem[] | undefined;
    articlesV2: CatalogItemFull[] | undefined;
    messageType: MessageType;
    mentionedAgents: Agent[];
    dueDate: Date | undefined;
};

type ArticlesState = {
    articles: CatalogArticleItem[] | undefined;
    addArticle: (article: CatalogArticleItem) => void;
    removeArticle: ((article: CatalogArticleItem) => void) | undefined;
};

type ArticlesV2State = {
    articlesV2: CatalogItemFull[] | undefined;
    addArticleV2: (article: CatalogItemFull) => void;
    removeArticleV2: ((article: CatalogItemFull) => void) | undefined;
};

export type MessageState = {
    contextKey: string | undefined;
    messageType: MessageType;
    setMessageType: (t: MessageType) => void;
    replyText: string | undefined;
    setReplyText: (t: string | undefined) => void;
    replyHtml: string | undefined;
    setReplyHtml: (t: string | undefined) => void;
    bookings: BookingInMessage[] | undefined;
    addBooking: (booking: Booking) => void;
    removeBooking: ((booking: BookingInMessage) => void) | undefined;
    attachments: AttachmentWithFile[] | undefined;
    addFilesToAttachments: (files: FileList | null) => void;
    removeAttachment: ((attachment: AttachmentWithFile) => void) | undefined;
    clearChatContext: () => void;
} & ArticlesState &
    ArticlesV2State;

export type ChatContextState = {
    changeContextKey: (id: string) => void;
} & MessageState;

type KeyedState<T> = {
    set?: SetState<T>;
    states: Record<string, T>;
};

const messageTypeContext: KeyedState<MessageType> = {
    states: {},
};
const replyTextContext: KeyedState<string | undefined> = {
    states: {},
};
const replyHtmlContext: KeyedState<string | undefined> = {
    states: {},
};
const bookingsContext: KeyedState<BookingInMessage[]> = {
    states: {},
};
const attachmentsContext: KeyedState<AttachmentWithFile[]> = {
    states: {},
};
const articlesContext: KeyedState<CatalogArticleItem[]> = {
    states: {},
};
const articlesV2Context: KeyedState<CatalogItemFull[]> = {
    states: {},
};

function setCurrentValue<T>(stateMap: KeyedState<T>, value: T): void {
    if (stateMap.set) {
        stateMap.set(value);
    }
}

function switchContext<T>(key: string, stateMap: KeyedState<T>): void {
    setCurrentValue(stateMap, stateMap.states[key]);
}

function setValue<T>(
    stateMap: KeyedState<T>,
    key: string | undefined,
): (value: T) => void {
    return (value: T) => {
        if (key) {
            stateMap.states[key] = value;
            setCurrentValue(stateMap, value);
        }
    };
}

export function getCurrentCatalogItems(key: string): CatalogItemFull[] {
    return articlesV2Context.states[key] || [];
}

export function createContextKey(
    member: MemberDataFirestoreRecord,
    request: Request | undefined,
) {
    return `${member.id}/${request ? request.id : ""}`;
}

export function createChatContextState(
    keyState: Stateful<string | undefined>,
    messageTypeState: Stateful<MessageType>,
    replyTextState: Stateful<string | undefined>,
    replyHtmlState: Stateful<string | undefined>,
    bookingsState: Stateful<BookingInMessage[]>,
    attachmentsState: Stateful<AttachmentWithFile[]>,
    articlesState: Stateful<CatalogArticleItem[]>,
    articlesV2State: Stateful<CatalogItemFull[]>,
): ChatContextState {
    const [contextKey, setContextKey] = keyState;
    const [messageType, setMessageTypeState] = messageTypeState;
    const [replyText, setReplyTextState] = replyTextState;
    const [replyHtml, setReplyHtmlState] = replyHtmlState;
    const [bookings, setBookingsState] = bookingsState;
    const [attachments, setAttachmentsState] = attachmentsState;
    const [articles, setArticlesState] = articlesState;
    const [articlesV2, setArticlesStateV2] = articlesV2State;

    messageTypeContext.set = setMessageTypeState;
    replyTextContext.set = setReplyTextState;
    replyHtmlContext.set = setReplyHtmlState;
    bookingsContext.set = setBookingsState;
    attachmentsContext.set = setAttachmentsState;
    articlesContext.set = setArticlesState;
    articlesV2Context.set = setArticlesStateV2;

    const setMessageType = setValue(messageTypeContext, contextKey);
    const setReplyText = setValue(replyTextContext, contextKey);
    const setReplyHtml = setValue(replyHtmlContext, contextKey);
    const setBookings = setValue(bookingsContext, contextKey);
    const setAttachments = setValue(attachmentsContext, contextKey);
    const setArticles = setValue(articlesContext, contextKey);
    const setArticlesV2 = setValue(articlesV2Context, contextKey);

    const changeContextKey = (newKey: string) => {
        setContextKey(newKey);
        switchContext(newKey, messageTypeContext);
        switchContext(newKey, replyTextContext);
        switchContext(newKey, replyHtmlContext);
        switchContext(newKey, bookingsContext);
        switchContext(newKey, attachmentsContext);
        switchContext(newKey, articlesContext);
        switchContext(newKey, articlesV2Context);
        if (!messageType) {
            setMessageType("message");
        }
    };

    if (!messageType) {
        setMessageType("message");
    }

    const addBooking: (b: Booking) => void = (booking: Booking) => {
        if (bookings) {
            if (bookings.length >= 30) {
                window.alert("Maximum count for bookings in a single message is 30.");
                return;
            }
            setBookings(addToBookingArray(booking, bookings));
        } else {
            setBookings([booking]);
        }
    };

    const removeBooking = (b: BookingInMessage) => {
        if (bookings) {
            setBookings([...bookings.filter((booking) => booking.id !== b.id)]);
        }
    };

    const addFilesToAttachments: (files: FileList | null) => void = (
        files: FileList | null,
    ) => {
        if (files) {
            const newAttachments: AttachmentWithFile[] = [];
            for (let i = 0; i < files.length; i++) {
                newAttachments.push(attachmentFromFile(files[i]));
            }
            if (attachments) {
                setAttachments([...attachments, ...newAttachments]);
            } else {
                setAttachments(newAttachments);
            }
        }
    };

    const removeAttachment = (a: AttachmentWithFile) => {
        if (attachments) {
            setAttachments([
                ...attachments.filter((attachment) => attachment.id !== a.id),
            ]);
        }
    };

    const addArticle = (article: CatalogArticleItem) => {
        if (articles) {
            setArticles([...articles.filter((a) => a.uuid !== article.uuid), article]);
        } else {
            setArticles([article]);
        }
    };

    const removeArticle = (article: CatalogArticleItem) => {
        if (articles) {
            setArticles([...articles.filter((a) => article.uuid !== a.uuid)]);
        }
    };

    const addArticleV2 = (article: CatalogItemFull) => {
        if (articlesV2) {
            setArticlesV2([...articlesV2.filter((a) => a.id !== article.id), article]);
        } else {
            setArticlesV2([article]);
        }
    };

    const removeArticleV2 = (article: CatalogItemFull) => {
        if (articlesV2) {
            setArticlesV2([...articlesV2.filter((a) => article.id !== a.id)]);
        }
    };

    const clearChatContext: () => void = () => {
        setReplyText(undefined);
        setReplyHtml(undefined);
        setBookings([]);
        setArticles([]);
        setArticlesV2([]);
        setAttachments([]);
    };

    return {
        contextKey,
        messageType,
        setMessageType,
        changeContextKey,
        replyText,
        setReplyText,
        replyHtml,
        setReplyHtml,
        bookings,
        addBooking,
        removeBooking,
        attachments,
        addFilesToAttachments,
        removeAttachment,
        articles,
        articlesV2,
        addArticle,
        removeArticle,
        addArticleV2,
        removeArticleV2,
        clearChatContext,
    };
}

/* export const ChatContext: React.Context<ChatContextState> =
    createContext<ChatContextState>({
        contextKey: "",
        changeContextKey: () => {},
        messageType: "message",
        setMessageType: () => {},
        replyText: undefined,
        setReplyText: () => {},
        replyHtml: undefined,
        setReplyHtml: () => {},
        bookings: [],
        addBooking: () => {},
        removeBooking: () => {},
        attachments: [],
        addFilesToAttachments: () => {},
        removeAttachment: () => {},
        articles: [],
        articlesV2: [],
        addArticle: () => {},
        removeArticle: () => {},
        addArticleV2: () => {},
        removeArticleV2: () => {},
        clearChatContext: () => {},
    }); */

const uploadAttachments = async (
    attachmentsToUpload: AttachmentWithFile[],
    isInternalNote: boolean,
    conversation: Conversation,
) => {
    try {
        const [alreadyUploadedAttachments, unUploadedAttachments] =
            partition<AttachmentWithFile>(
                attachmentsToUpload,
                (attachmentHasFile) => !attachmentHasFile.file,
            );
        const uploadedAttachments: AttachmentWithFile[] = alreadyUploadedAttachments;

        for (const attachment of unUploadedAttachments) {
            const uploadTo = conversationFileRef(
                randomUUIDFilename(),
                conversation.member,
                conversation.id,
            );

            const allowAllFiletypes = undefined;
            const allowedFileTypes = isInternalNote
                ? allowAllFiletypes
                : supportedFileTypes;

            if (attachment.file) {
                uploadedAttachments.push(
                    await uploadFile(attachment.file, uploadTo, allowedFileTypes),
                );
            }
        }
        return uploadedAttachments;
    } catch (error) {
        throw error;
    }
};

export const createNewMessage = async (
    content: MessageInputContent,
    request: Request | undefined,
    currentAgent: Agent,
    conversation: Conversation,
    member: MemberDataFirestoreRecord,
): Promise<AgentNewMessagePayload> => {
    const {
        replyText,
        replyHtml,
        attachments,
        bookings,
        articles,
        articlesV2,
        messageType,
    } = content;

    const requestId = request ? request.id : null;
    const requestTitle = request ? request.title : null;
    const senderFirstName = currentAgent.firstName;
    const isInternalNote = messageType === "internal_note";

    const messageBody: AgentNewMessagePayload = {
        message: replyText || "",
        messageHtml: replyHtml && replyHtml.length > 0 ? replyHtml : null,
        senderFirstName,
        sentByAgent: true,
        request: requestId,
        requestTitle,
        member: member.id,
        memberName: `${member.firstName} ${member.lastName}`,
        editedAt: null,
        editedBy: null,
        editorName: null,
        ...(bookings && bookings.length > 0 ? { bookingSuggestions: bookings } : {}),
    };

    let newMessage: AgentNewMessagePayload = {
        ...messageBody,
    };

    if (attachments && attachments.length > 0) {
        const uploadedAttachments = await uploadAttachments(
            attachments,
            isInternalNote,
            conversation,
        );
        newMessage = {
            ...newMessage,
            attachments: uploadedAttachments,
        };
    }

    if (articles && articles.length > 0) {
        newMessage = {
            ...newMessage,
            articles,
        };
    }

    if (articlesV2 && articlesV2.length > 0) {
        newMessage = {
            ...newMessage,
            articlesV2: articlesV2.map((article) =>
                catalogItemToCatalogItemPreview(
                    article,
                    getLocale(member.serviceLanguage),
                ),
            ),
        };
    }

    return newMessage;
};
